import { css, StyleSheet } from 'aphrodite/no-important';
import { RATIO } from 'mm-theme-configuration/dist/consts';
import { ImageStyleSheet } from './image.types';
import { useTheme } from '../../../theming/useTheme';
import { ArticleBlockThemeProps, componentConfigurationKey as articleBlockComponentConfigurationKey } from '../../articleComponents/ArticleBlock.theme';
import { AdditionalStyleSheet } from '../../components.utils';

export type ImageWidth = 16 | 50 | 80 | 110 | 150 | 180 | 335 | 360 | 540 | 620 | 720 | 1080 | 1200 | 1440;
type Dpr = 1 | 2 | 3;

export interface ImageWidths {
  small: ImageWidth;
  medium: ImageWidth;
  large: ImageWidth;
}

export interface CroppingData {
  x: number;
  y: number;
  width: number;
  height: number;
}

const calculateImageUrl: (host: string, path: string, manipulation?: string) => string = (host, path, manipulation?) => `${host + manipulation}/${path}`;
const isHighDprInLargeImage = (width: number, dpr: number) => width >= 600 && dpr > 2;

const addCroppingParamsToPath = (path: string, croppingData: CroppingData | null) => {
  if (croppingData) {
    return `c_crop,w_${croppingData.width},h_${croppingData.height},x_${croppingData.x},y_${croppingData.y}/${path}`;
  }
  return path;
};

export const createImageSrc = (host: string, path: string, width: ImageWidth, aspectRatio: RATIO, dpr: Dpr, croppingData: CroppingData | null): string => {
  if (isHighDprInLargeImage(width, dpr)) {
    return createImageSrc(host, path, width, aspectRatio, 2, croppingData);
  }

  if (aspectRatio && aspectRatio.original === true) {
    return calculateImageUrl(host, path, addCroppingParamsToPath('f_auto,q_auto,g_auto', null));
  }

  return calculateImageUrl(host, path, addCroppingParamsToPath(`c_fill,w_${width * dpr},ar_${aspectRatio.x}:${aspectRatio.y},f_auto,q_auto,g_auto`, croppingData));
};

// The image-delivery-service leaks an editor crop's north-west offset into the
// following c_fill gravity, cropping the image to the top-left corner when the
// requested display ratio diverges from the editorial crop (e.g. a 4:5 card
// built from a 16:9 editorial crop). When a requested ratio diverges, drop the
// crop for that ratio so g_auto smart-crops the original: a single stage, so
// there is no offset to leak, and the fill has the full frame. Ratios that match
// the crop keep it, preserving the editor's framing where it renders correctly.
// The tolerance is deliberately loose (15%) so only clearly-divergent ratios
// (portrait/square from a landscape crop) drop; mild landscape variations such
// as 2:1 from a 16:9 crop keep the editorial crop, where the leak is negligible.
const CROP_RATIO_DIVERGENCE_TOLERANCE = 0.15;

export const editorCropDivergesFromRatio = (cropping: CroppingData | null | undefined, aspectRatio: RATIO | undefined): boolean => {
  if (!cropping || !cropping.width || !cropping.height || !aspectRatio || aspectRatio.original === true || !aspectRatio.x || !aspectRatio.y) {
    return false;
  }
  const cropRatio = cropping.width / cropping.height;
  const targetRatio = aspectRatio.x / aspectRatio.y;
  return Math.abs(cropRatio - targetRatio) > targetRatio * CROP_RATIO_DIVERGENCE_TOLERANCE;
};

export const getImageSrcAndSrcSet = (imageAspectRatios: ImageAspectRatios, imageWidths: ImageWidths, host: string, path: string, cropping: CroppingData | null) => {
  const encodedPathForSrcset = host && host.includes('minutemediacdn') ? encodeURIComponent(path) : path;
  // Decide per breakpoint: a hero can be portrait on mobile (diverges from a
  // landscape editorial crop, so drop it) yet landscape on desktop (matches, so
  // keep it). An all-or-nothing decision would keep the crop on mobile and
  // reproduce the top-left bug there.
  const croppingForRatio = (aspectRatio: RATIO) => (editorCropDivergesFromRatio(cropping, aspectRatio) ? null : cropping);
  const smallCropping = croppingForRatio(imageAspectRatios.small);
  const mediumCropping = croppingForRatio(imageAspectRatios.medium);
  const largeCropping = croppingForRatio(imageAspectRatios.large);
  const lowResCropping = croppingForRatio({ x: 16, y: 9 });

  return {
    srcSetSmallScreen: `${createImageSrc(host, encodedPathForSrcset, imageWidths.small, imageAspectRatios.small, 1, smallCropping)} 1x, ${createImageSrc(host, encodedPathForSrcset, imageWidths.small, imageAspectRatios.small, 2, smallCropping)} 2x, ${createImageSrc(host, encodedPathForSrcset, imageWidths.small, imageAspectRatios.small, 3, smallCropping)} 3x`,
    srcSetMediumScreen: `${createImageSrc(host, encodedPathForSrcset, imageWidths.medium, imageAspectRatios.medium, 1, mediumCropping)} 1x, ${createImageSrc(host, encodedPathForSrcset, imageWidths.medium, imageAspectRatios.medium, 2, mediumCropping)} 2x, ${createImageSrc(host, encodedPathForSrcset, imageWidths.medium, imageAspectRatios.medium, 3, mediumCropping)} 3x`,
    srcSetLargeScreen: `${createImageSrc(host, encodedPathForSrcset, imageWidths.large, imageAspectRatios.large, 1, largeCropping)} 1x, ${createImageSrc(host, encodedPathForSrcset, imageWidths.large, imageAspectRatios.large, 2, largeCropping)} 2x, ${createImageSrc(host, encodedPathForSrcset, imageWidths.large, imageAspectRatios.large, 3, largeCropping)} 3x`,
    lowResUrl: createImageSrc(host, path, 16, { x: 16, y: 9 }, 1, lowResCropping),
    fallbackSrc: createImageSrc(host, path, imageWidths.medium, imageAspectRatios.medium, 1, mediumCropping),
  };
};

export const originalImageSize = StyleSheet.create({
  style: {
    position: 'relative',
  },
}) as AdditionalStyleSheet;

export const imageStyle = StyleSheet.create({
  base: {
    position: 'absolute',
    top: '0',
    left: '0',
    bottom: '0',
    minWidth: '100%',
    maxWidth: '100%',
    maxHeight: '100%',
    transition: 'opacity 0.2s ease-in-out',
    objectFit: 'cover',
    minHeight: '100%',
  },
}) as ImageStyleSheet;

export const getImageCaption = (credit: string | undefined, caption: string | undefined): string => {
  return [caption, credit].filter(value => {
    return typeof value === 'string' && value.trim() !== '';
  }).join(' | ');
};

export const createImageWidths = (small: ImageWidth, medium: ImageWidth, large: ImageWidth) => ({
  small,
  medium,
  large,
});

export type ImageHeightCalculationMethodsTypes = 'ASPECT_RATIO' | '100%_HEIGHT';

export interface ImageHeightCalculationMethods {
  small: ImageHeightCalculationMethodsTypes;
  medium: ImageHeightCalculationMethodsTypes;
  large: ImageHeightCalculationMethodsTypes;
}

export const createImageHeightCalculationMethods = (small: ImageHeightCalculationMethodsTypes, medium: ImageHeightCalculationMethodsTypes, large: ImageHeightCalculationMethodsTypes) => ({
  small,
  medium,
  large,
});

export interface ImageAspectRatios {
  small: RATIO;
  medium: RATIO;
  large: RATIO;
}

export const createImageAspectRatios = (small: RATIO, medium: RATIO, large: RATIO) => ({
  small,
  medium,
  large,
});

const getCreditStyle = (linkColor: string) => StyleSheet.create({
  style: {
    color: linkColor,
    textDecoration: 'none',
  },
}).style;

export const useTransformCreditHTML = (credit: string | undefined) => {
  const { creditLinkColor } = useTheme<ArticleBlockThemeProps>(articleBlockComponentConfigurationKey);
  const styleForCredit = css(getCreditStyle(creditLinkColor));
  if (!credit) {
    return '';
  }
  return credit
    .replace(/<a/g, `<a class="${styleForCredit}"`)
    .replace(/div/g, 'span');
};
